import type React from 'react';
import { cn } from '../../utils/cn.js';

interface ChatPageProps {
  /** One compact row: conversation title, status, members, actions. */
  header?: React.ReactNode;
  /** The conversation list (left), when the page has one. Hidden below `xl`; offer it from the header there. */
  list?: React.ReactNode;
  /** The assistant-ui `Thread` (messages + composer); it scrolls on its own. */
  thread: React.ReactNode;
  /** The context rail (right): members, linked entities, artifacts. Hidden below `xl`; offer it from the header. */
  rail?: React.ReactNode;
  className?: string;
}

/**
 * The conversation template: a full-height thread with an optional conversation list and context rail. The thread is
 * the only thing that scrolls; the composer stays pinned at its bottom.
 */
export function ChatPage({ header, list, thread, rail, className }: ChatPageProps) {
  return (
    <div className={cn('flex h-(--page-height) min-h-[480px] flex-col gap-4', className)}>
      {header && <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">{header}</div>}
      <div
        className={cn(
          'grid min-h-0 flex-1 grid-cols-1 gap-4',
          list && rail && 'xl:grid-cols-[260px_minmax(0,1fr)_320px]',
          list && !rail && 'xl:grid-cols-[260px_minmax(0,1fr)]',
          !list && rail && 'xl:grid-cols-[minmax(0,1fr)_320px]',
        )}
      >
        {list && (
          <nav aria-label="Conversations" className="hidden min-h-0 overflow-y-auto xl:block">
            {list}
          </nav>
        )}
        <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-card-border bg-surface">
          {thread}
        </section>
        {rail && <aside className="hidden min-h-0 overflow-y-auto xl:block">{rail}</aside>}
      </div>
    </div>
  );
}
